import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PageMeta from "../../components/common/PageMeta";
import Paper from "../../components/ui/customs/Paper";
import { apiRequestWithAuth } from "../../utils/api";
import SelectV2 from "../../components/form/SelectV2";
import { skillTypes } from "../../types/constants";
import { FileUp, X, CheckCircle, AlertCircle } from "lucide-react";

type Category = {
    value: string;
    label: string;
};

type FormData = {
    title: string;
    categoryId: string;
    skillId: string;
    timeLimit: number;
};

const defaultForm: FormData = {
    title: "",
    categoryId: "",
    skillId: "",
    timeLimit: 60,
};

export default function ImportExam() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<FormData>(defaultForm);
    const [categories, setCategories] = useState<Category[]>([]);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<"idle" | "success" | "error">("idle");
    const [errorMessage, setErrorMessage] = useState("");
    const hasFetchedCategories = useRef(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const fetchCategories = async () => {
        if (hasFetchedCategories.current) return;

        hasFetchedCategories.current = true;
        try {
            const res = await apiRequestWithAuth("/category", "GET", {});
            const rawCategories = res.result;

            // B1: gom nhóm theo parentId
            const mapByParent: Record<string, any[]> = {};
            rawCategories.forEach((cat: any) => {
                const parentId = cat.parentId ?? "root";
                if (!mapByParent[parentId]) mapByParent[parentId] = [];
                mapByParent[parentId].push(cat);
            });

            // B2: hàm đệ quy để build tree phẳng + prefix
            const buildHierarchy = (parentId = "root", prefix = ""): any[] => {
                const children = mapByParent[parentId] || [];
                return children.flatMap((child) => [
                    {
                        value: child.id.toString(),
                        label: prefix + child.title,
                    },
                    ...buildHierarchy(child.id.toString(), prefix + "-- "),
                ]);
            };

            // B3: set categories
            setCategories(buildHierarchy());
        } catch (error) {
            console.error("Lỗi lấy danh mục:", error);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: name === "timeLimit" ? parseInt(value) || 0 : value,
        }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file type
            const validTypes = [
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "application/msword",
            ];
            if (!validTypes.includes(file.type)) {
                setErrorMessage("Vui lòng chọn file MS Word (.doc hoặc .docx)");
                setUploadStatus("error");
                return;
            }

            setSelectedFile(file);
            setUploadStatus("idle");
            setErrorMessage("");
        }
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setUploadStatus("idle");
        setErrorMessage("");
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const file = e.dataTransfer.files?.[0];
        if (file) {
            const validTypes = [
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "application/msword",
            ];
            if (!validTypes.includes(file.type)) {
                setErrorMessage("Vui lòng chọn file MS Word (.doc hoặc .docx)");
                setUploadStatus("error");
                return;
            }

            setSelectedFile(file);
            setUploadStatus("idle");
            setErrorMessage("");
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validation
        if (!formData.title.trim()) {
            setErrorMessage("Vui lòng nhập tiêu đề bài thi");
            setUploadStatus("error");
            return;
        }

        if (!formData.categoryId) {
            setErrorMessage("Vui lòng chọn danh mục");
            setUploadStatus("error");
            return;
        }


        if (!selectedFile) {
            setErrorMessage("Vui lòng chọn file Word để upload");
            setUploadStatus("error");
            return;
        }

        setUploading(true);
        setErrorMessage("");

        try {
            const formDataUpload = new FormData();
            formDataUpload.append("file", selectedFile);
            formDataUpload.append("title", formData.title);
            formDataUpload.append("categoryId", formData.categoryId);
            formDataUpload.append("skillId", formData.skillId);
            formDataUpload.append("timeLimit", formData.timeLimit.toString());

            const res = await apiRequestWithAuth("/quiz/import-docx", "POST", formDataUpload);

            if (res.messageCode === 200) {
                setUploadStatus("success");
                setTimeout(() => {
                    navigate(`/exams/${res.result.id}/update`);
                }, 1500);
            } else {
                setUploadStatus("error");
                setErrorMessage(res.message || "Có lỗi xảy ra khi tạo bài thi");
            }
        } catch (error: any) {
            setUploadStatus("error");
            setErrorMessage(error.message || "Có lỗi xảy ra khi upload file");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div>
            <PageMeta title="Import bài thi từ Word" description="Upload file MS Word để tạo bài thi trắc nghiệm" />
            <div className="container mx-auto max-w-4xl mb-4">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Import bài thi từ Word
                    </h1>
                    <button
                        onClick={() => navigate("/exams")}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                    >
                        Quay lại
                    </button>
                </div>

                <Paper elevation={2} className="p-6">
                    <form onSubmit={handleSubmit}>
                        {/* Title */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                                Tiêu đề bài thi <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg p-3 text-sm shadow-sm focus:ring focus:ring-indigo-200 focus:outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                placeholder="Nhập tiêu đề bài thi..."
                                required
                            />
                        </div>

                        {/* Category, Skill Type, Time Limit */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                                    Danh mục <span className="text-red-500">*</span>
                                </label>
                                <SelectV2
                                    options={categories}
                                    placeholder="Chọn danh mục"
                                    defaultValue={formData.categoryId}
                                    onChange={(val) => {
                                        setFormData((prev) => ({ ...prev, categoryId: val }));
                                    }}
                                    className="dark:bg-gray-800"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                                    Loại
                                </label>
                                <SelectV2
                                    options={skillTypes}
                                    placeholder="Chọn loại"
                                    defaultValue={formData.skillId}
                                    onChange={(val) => {
                                        setFormData((prev) => ({ ...prev, skillId: val }));
                                    }}
                                    className="dark:bg-gray-800"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                                    Thời gian làm bài (phút)
                                </label>
                                <input
                                    type="number"
                                    name="timeLimit"
                                    min="1"
                                    max="300"
                                    value={formData.timeLimit}
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg p-2 text-sm shadow-sm focus:ring focus:ring-indigo-200 focus:outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                    placeholder="60"
                                />
                            </div>
                        </div>

                        {/* File Upload Area */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                                File Word <span className="text-red-500">*</span>
                            </label>

                            {!selectedFile ? (
                                <div
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-indigo-400 transition-colors dark:border-gray-600 dark:hover:border-indigo-500"
                                >
                                    <FileUp className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                        Kéo thả file Word vào đây hoặc
                                    </p>
                                    <label className="cursor-pointer">
                                        <span className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 inline-block">
                                            Chọn file
                                        </span>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                            onChange={handleFileChange}
                                            className="hidden"
                                        />
                                    </label>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                        Hỗ trợ file .doc, .docx
                                    </p>
                                </div>
                            ) : (
                                <div className="border border-gray-300 rounded-lg p-4 dark:border-gray-600">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3">
                                            <div className="flex-shrink-0">
                                                <FileUp className="h-8 w-8 text-indigo-600" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {selectedFile.name}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleRemoveFile}
                                            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                        >
                                            <X className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Status Messages */}
                        {uploadStatus === "success" && (
                            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2 dark:bg-green-900/20 dark:border-green-800">
                                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                                <p className="text-sm text-green-800 dark:text-green-300">
                                    Import bài thi thành công! Đang chuyển hướng...
                                </p>
                            </div>
                        )}

                        {uploadStatus === "error" && errorMessage && (
                            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2 dark:bg-red-900/20 dark:border-red-800">
                                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                                <p className="text-sm text-red-800 dark:text-red-300">{errorMessage}</p>
                            </div>
                        )}

                        {/* Submit Button */}
                        <div className="flex justify-end space-x-3">
                            <button
                                type="button"
                                onClick={() => navigate("/exams")}
                                className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                            >
                                Hủy
                            </button>
                            <button
                                type="submit"
                                disabled={uploading}
                                className={`px-6 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                                    uploading ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                            >
                                {uploading ? "Đang xử lý..." : "Import bài thi"}
                            </button>
                        </div>
                    </form>
                </Paper>

                {/* Instructions */}
                <Paper elevation={1} className="p-6 mt-6">
                    <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                        Hướng dẫn sử dụng
                    </h3>
                    <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                        <li className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>File Word cần được định dạng đúng với cấu trúc câu hỏi trắc nghiệm</span>
                        </li>
                        <li className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>Các công thức toán học trong file sẽ được giữ nguyên định dạng</span>
                        </li>
                        <li className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>Sau khi import thành công, bạn có thể chỉnh sửa bài thi trong trang cập nhật</span>
                        </li>
                        <li className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>Kích thước file tối đa: 10MB</span>
                        </li>
                    </ul>
                </Paper>
            </div>
        </div>
    );
}

